import React, { useRef } from 'react';

import Clickable from 'ds/Clickable';
import Icon from 'ds/Icon';
import Layout from 'ds/Layout';
import Text, { Size } from 'ds/Text';
import { EditablePdf } from 'shared';

import MessagePdfUploadThumbnail from './MessagePdfUploadThumbnail';

interface Props {
  files: EditablePdf[];
  onChange: (files: File[]) => void;
  onDeleteClick?: (pdf: EditablePdf) => void;
  disabled?: boolean;
  maxFiles?: number;
  label?: string;
  labelSize?: Size;
  fileDimension?: number;
}

const MessagePdfUploader: React.FC<Props> = ({
  onChange,
  onDeleteClick,
  disabled,
  maxFiles = 5,
  label,
  labelSize = 'body2',
  fileDimension = 48,
  ...props
}) => {
  const hiddenFileInputRef = useRef<HTMLInputElement>(null);
  const showFileChooser = () => hiddenFileInputRef.current?.click();

  const files = props.files.filter(p => !p._destroy);
  const gap = 6;

  return (
    <Layout>
      {files.length ? (
        <Layout flex wrap position="relative" left={-gap} width="calc(100% + 12px)">
          {files.map(pdf => {
            return (
              <MessagePdfUploadThumbnail
                key={pdf.id || pdf.url || pdf.tempId}
                containerProps={{
                  margin: gap,
                  width: 272,
                  height: 48
                }}
                disabled={disabled}
                pdf={pdf}
                onDeleteClick={!!onDeleteClick ? () => onDeleteClick(pdf) : undefined}
              />
            );
          })}
          {files.length < maxFiles && (
            <Clickable onClick={showFileChooser} style={{ borderRadius: 8 }} disabled={disabled}>
              <Layout
                align="center"
                justify="center"
                color="gray-25"
                margin={gap}
                width={fileDimension}
                height={fileDimension}
                borderRadius={8}
              >
                <Icon name="plus" size="md" color="gray-200" />
              </Layout>
            </Clickable>
          )}
        </Layout>
      ) : (
        <Clickable onClick={showFileChooser} style={{ display: 'flex', alignItems: 'center' }} disabled={disabled}>
          <Icon name="attachment" size="xs" color="blue-500" />
          <Text size={labelSize} color="blue-500">
            {label || 'Attach PDF'}
          </Text>
        </Clickable>
      )}
      <input
        ref={hiddenFileInputRef}
        type="file"
        style={{ display: 'none' }}
        accept=".pdf"
        multiple={maxFiles !== 1}
        onChange={event => {
          const files = event.target.files;

          if (!files) return;

          onChange(Array.from(files).slice(0, maxFiles - files.length));
        }}
      />
    </Layout>
  );
};

export default MessagePdfUploader;
